import { useEffect, useMemo, useState } from "react";
import { Button, Chip, IconButton, InputAdornment, Paper, Stack, TextField, Typography, Checkbox, FormControlLabel, Divider, Box } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import EditIcon from "@/assets/svgs/edit.svg";
import DeleteIcon from "@/assets/svgs/garbage.svg";
import { ModalV2 } from "@/components";
import { DatePicker } from "@/components";
import { useGetExclusionListsQuery, useGetExclusionListQuery, useCreateExclusionListMutation, useUpdateExclusionListMutation, useDeleteExclusionListMutation, ExclusionList } from "@/services/product-catalogue/exclusion-lists-api";
import { useGetDutyColumnsQuery, AUTHORITIES } from "@/services/product-catalogue/duty-columns-api";
import { getAuthorityStyle } from "../rule-form-section/helpers";
import VirtualizedChipList from "@/components/virtualized-chip-list";

type FormState = {
  _id?: string;
  name: string;
  targetColumnIds: string[];
  codes: string[];
  effectRatePct: string;
  active: boolean;
  startDate?: string | null;
  endDate?: string | null;
};

const ExclusionListsManager = () => {
  const { data, isLoading } = useGetExclusionListsQuery({ pageNo: 1, pageSize: 500, active: undefined });
  const dutyColumns = useGetDutyColumnsQuery({ pageNo: 1, pageSize: 500 });
  const [presetAuthority, setPresetAuthority] = useState<string | null>(null);
  const authFiltered = useGetDutyColumnsQuery(
    useMemo(
      () => ({
        pageNo: 1,
        pageSize: 500,
        authorities: presetAuthority ? [presetAuthority] as any : undefined,
      }),
      [presetAuthority]
    )
  );
  const [createExclusionList, { isLoading: isCreating }] = useCreateExclusionListMutation();
  const [updateExclusionList, { isLoading: isUpdating }] = useUpdateExclusionListMutation();
  const [deleteExclusionList] = useDeleteExclusionListMutation();

  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>({ name: "", targetColumnIds: [], codes: [], effectRatePct: "0", active: true, startDate: null, endDate: null });
  const [codeInput, setCodeInput] = useState("");
  const [columnSearch, setColumnSearch] = useState("");

  const rows = useMemo(() => (data?.data || []), [data]);

  const handleEdit = (row: ExclusionList) => {
    setSelectedId(row._id);
    setOpen(true);
  };

  const handleDelete = async (row: ExclusionList) => {
    await deleteExclusionList({ id: row._id });
  };

  const handleCreate = () => {
    setSelectedId(null);
    setForm({ name: "", targetColumnIds: [], codes: [], effectRatePct: "0", active: true, startDate: null, endDate: null });
    setPresetAuthority(null);
    setOpen(true);
  };

  const parseCodes = (text: string) =>
    text
      .split(/[\s,\n\r\t;]+/g)
      .map(s => s.trim().replace(/[.\s-]/g, ""))
      .filter(code => code.length >= 2 && code.length <= 10);

  const addCodesFromText = (text: string) => {
    const parsed = parseCodes(text);
    if (parsed.length === 0) return;
    const existing = new Set(form.codes);
    const merged = [...form.codes, ...parsed.filter(c => !existing.has(c))];
    setForm(f => ({ ...f, codes: merged }));
  };

  const save = async () => {
    const effectRate = Math.max(0, Number(form.effectRatePct || "0")) / 100;
    const payload = {
      name: form.name.trim(),
      targetColumnIds: form.targetColumnIds,
      codes: form.codes,
      active: form.active,
      effectRate,
      // Anchor to UTC boundaries to avoid timezone drift
      startDate: form.startDate ? `${form.startDate}T00:00:00.000Z` : undefined,
      endDate: form.endDate ? `${form.endDate}T23:59:59.999Z` : undefined,
    };
    if (form._id) {
      await updateExclusionList({ id: form._id, input: payload });
    } else {
      await createExclusionList({ input: payload });
    }
    setOpen(false);
  };

  const allColumns = dutyColumns.data?.data || [];
  const filteredColumns = useMemo(() => {
    if (!columnSearch.trim()) return allColumns;
    const q = columnSearch.toLowerCase();
    return allColumns.filter(c => c.name.toLowerCase().includes(q));
  }, [allColumns, columnSearch]);

  const toggleColumn = (id: string) => {
    setForm(f => {
      const set = new Set(f.targetColumnIds);
      if (set.has(id)) set.delete(id); else set.add(id);
      return { ...f, targetColumnIds: Array.from(set) };
    });
  };

  useEffect(() => {
    if (!presetAuthority) return;
    const list = (authFiltered.data?.data || []);
    if (!list.length) return;
    const ids = list.map((c: any) => c._id);
    if (ids.length === 0) return;
    setForm((f) => ({ ...f, targetColumnIds: Array.from(new Set([...f.targetColumnIds, ...ids])) }));
    // Do not keep preset sticky; allow subsequent clicks to fetch again when needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authFiltered.data?.data]);

  const selectByAuthority = (authority: string) => {
    // Force re-run even if the same authority is clicked repeatedly
    setPresetAuthority(null);
    setTimeout(() => setPresetAuthority(authority), 0);
  };

  // Load detail when editing
  const detail = useGetExclusionListQuery({ id: selectedId as string }, { skip: !selectedId || !open });
  useEffect(() => {
    if (!detail.data) return;
    setForm({
      _id: detail.data._id,
      name: detail.data.name,
      targetColumnIds: detail.data.targetColumnIds || [],
      codes: detail.data.codes || [],
      effectRatePct: typeof detail.data.effectRate === "number" ? String((detail.data.effectRate * 100).toFixed(2)) : "0",
      active: detail.data.active ?? true,
      // Normalize to YYYY-MM-DD to display exact stored date without TZ shifts
      startDate: detail.data.startDate ? new Date(detail.data.startDate).toISOString().slice(0, 10) : null,
      endDate: detail.data.endDate ? new Date(detail.data.endDate).toISOString().slice(0, 10) : null,
    });
  }, [detail.data]);

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle1" fontWeight={700}>Exclusion Lists</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>New Exclusion List</Button>
      </Stack>
      <Stack spacing={1}>
        {isLoading && <Typography variant="body2" color="text.secondary">Loading...</Typography>}
        {!isLoading && rows.length === 0 && (
          <Typography variant="body2" color="text.secondary">No exclusion lists yet.</Typography>
        )}
        {!isLoading && rows.map((row) => {
          return (
            <Paper key={row._id} variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2" fontWeight={700}>{row.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Columns: {row.targetColumnCount ?? 0} • Codes: {row.codesCount ?? 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(() => {
                      const toLabel = (iso?: string) => {
                        if (!iso) return null;
                        const ymd = iso.slice(0, 10);
                        const [y, m, d] = ymd.split('-').map(n => Number(n));
                        const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
                        if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return ymd;
                        if (m < 1 || m > 12 || d < 1 || d > 31) return ymd;
                        if (!months[m - 1]) return ymd;
                        return `${months[m - 1]} ${d}, ${y}`;
                      };
                      const start = toLabel(row.startDate);
                      const end = toLabel(row.endDate);
                      return `Effective${start ? `: ${start}` : ': —'}${end ? ` → ${end}` : ''}`;
                    })()}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <IconButton size="small" onClick={() => handleEdit(row)}>
                    <img src={EditIcon} alt="Edit" style={{ width: 20, height: 20 }} />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
                    <img src={DeleteIcon} alt="Delete" style={{ width: 20, height: 20 }} />
                  </IconButton>
                </Stack>
              </Stack>
            </Paper>
          );
        })}
      </Stack>

      <ModalV2
        show={open}
        setShow={(value) => {
          if (typeof value === "function") {
            const next = value(open);
            setOpen(next);
            if (!next) setPresetAuthority(null);
          } else {
            setOpen(value);
            if (!value) setPresetAuthority(null);
          }
        }}
        title={form._id ? "Edit Exclusion List" : "Create Exclusion List"}
        subTitle={form._id ? "Modify columns, codes and the applied rate" : "Set up a new exclusion list with target columns and codes"}
        buttonText="Save"
        buttonAction={save}
        buttonLoader={isCreating || isUpdating}
        maxWidth="lg"
        modalStyles={{
          "& .MuiPaper-root": {
            maxWidth: "1100px",
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Stack spacing={2.5}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
              fullWidth
            />
            <Stack direction="row" spacing={2}>
              <DatePicker
                label="Start Date (optional)"
                value={form.startDate || null}
                onChangeValue={(d) => setForm(f => ({ ...f, startDate: d || null }))}
              />
              <DatePicker
                label="End Date (optional)"
                value={form.endDate || null}
                onChangeValue={(d) => setForm(f => ({ ...f, endDate: d || null }))}
              />
            </Stack>
            <TextField
              label="Effect Rate (%)"
              type="number"
              value={form.effectRatePct}
              onChange={(e) => setForm(f => ({ ...f, effectRatePct: e.target.value }))}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                inputProps: { min: 0, step: 0.1 },
              }}
              helperText="Applied rate when this exclusion triggers (0% = zeroed)."
              fullWidth
            />
            <Stack spacing={1}>
              <Typography variant="subtitle2" sx={{ color: '#0B494B', fontWeight: 700 }}>
                Authority Presets
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                {AUTHORITIES.map(auth => (
                  <Chip
                    key={auth}
                    label={auth}
                    onClick={() => selectByAuthority(auth)}
                    icon={getAuthorityStyle(auth).icon}
                    sx={{
                      backgroundColor: getAuthorityStyle(auth).color as any,
                      color: 'white',
                      '&:hover': { opacity: 0.92 }
                    }}
                    size="small"
                  />
                ))}
              </Stack>
            </Stack>
            <Divider />
            <Stack spacing={1}>
              <Typography variant="subtitle2" sx={{ color: '#1e40af', fontWeight: 700 }}>
                Target Duty Columns
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  backgroundColor: '#f0f9ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: 1.5,
                }}
              >
                <Stack spacing={1}>
                  <TextField
                    size="small"
                    placeholder="Search columns…"
                    value={columnSearch}
                    onChange={(e) => setColumnSearch(e.target.value)}
                  />
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          targetColumnIds: Array.from(
                            new Set([
                              ...f.targetColumnIds,
                              ...filteredColumns.map((c) => c._id),
                            ])
                          ),
                        }))
                      }
                    >
                      Select All
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setForm((f) => ({ ...f, targetColumnIds: [] }))}
                      disabled={form.targetColumnIds.length === 0}
                    >
                      Clear All
                    </Button>
                  </Stack>
                  <Paper variant="outlined" sx={{ maxHeight: 220, overflow: 'auto', p: 1, px: 2 }}>
                    <Stack spacing={0.5}>
                      {filteredColumns.map(col => (
                        <FormControlLabel
                          key={col._id}
                          control={
                            <Checkbox
                              size="small"
                              checked={form.targetColumnIds.includes(col._id)}
                              onChange={() => toggleColumn(col._id)}
                            />
                          }
                          label={col.name}
                        />
                      ))}
                    </Stack>
                  </Paper>
                  {form.targetColumnIds.length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      Selected: {form.targetColumnIds.length}
                    </Typography>
                  )}
                </Stack>
              </Paper>
            </Stack>
            <Divider />
            <Stack spacing={1}>
              <Typography variant="subtitle2" sx={{ color: '#15803d', fontWeight: 700 }}>
                HTS Codes (Prefix Match)
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  borderRadius: 1.5,
                }}
              >
                <TextField
                  label="Paste or type HTS Codes"
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value)}
                  onPaste={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    addCodesFromText(e.clipboardData.getData('text'));
                    setTimeout(() => setCodeInput(""), 50);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && codeInput.trim()) {
                      addCodesFromText(codeInput);
                      setCodeInput("");
                    }
                  }}
                  fullWidth
                  placeholder="e.g., 99038801, 99038802"
                  helperText="Punctuation ignored; supports 2–10 digit prefixes. Press Enter to add."
                />
                <Paper variant="outlined" sx={{ mt: 1.5, borderRadius: '6px', overflow: 'hidden', px: 2 }}>
                  <VirtualizedChipList
                    items={form.codes}
                    onDeleteItem={(code: string) => {
                      setForm(f => ({ ...f, codes: f.codes.filter(c => c !== code) }));
                    }}
                  />
                </Paper>
              </Paper>
            </Stack>
          </Stack>
        </Box>
      </ModalV2>
    </Stack>
  );
};

export default ExclusionListsManager;


